import React, {useEffect} from 'react'
import {observer} from 'mobx-react'
import ProcedureIntro from './ProcedureIntro'
import PropTypes from 'prop-types';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import './procedureMain.scss'
import ConditionalComponent from "../../Shared/ConditionalComponent";
import {procedureNodeStyleEnum, procedureNodeTypeEnum} from "../../../serverClientShared/sharedEnums";
import {Button, IconButton} from "@mui/material";
import PopupMediaSelect from "./ProcedureMedia/PopupMediaSelect";
import {EndNode} from "./EndNode";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import Utility from "../../../Utility";
import ProcedureSummary from "./ProcedureSummary";
import {isTablet} from "react-device-detect";
import {useStores} from "../../../Hooks";
import TtsMuteButton from "../../Shared/TtsMuteButton";
import {getProcedureText, getNodeMedia} from "../../../serverClientShared/sharedUtils";

const ProcedureMain = observer(props => {
    const {procedureStore, generalStore} = useStores();
    const {isPreview, fromEditor} = props;

    const currentNode = isPreview ? procedureStore.currentPreviewedNode : procedureStore.currentNode;
    const currentProcedure = isPreview ? procedureStore.currentPreviewedProcedure : procedureStore.currentProcedure;

    const {sessionViewerOpen, innerProcedureParentProcedureIds} = procedureStore;
    const currentStepIndex = isPreview ? procedureStore.currentPreviewedStepIndex : procedureStore.currentStepIndex;
    const isEndNode = !!currentNode && currentNode.NodeType === procedureNodeTypeEnum.END;
    const showIntro = (currentStepIndex === 0 && !innerProcedureParentProcedureIds.length) || (sessionViewerOpen && !!currentNode && currentNode.NodeType === procedureNodeTypeEnum.START)

    useEffect(() => {
        if (currentNode && currentNode.NodeType !== procedureNodeTypeEnum.START) {
            generalStore.forceStopDataPoll = true;
        }
        return () => {
            generalStore.forceStopDataPoll = false;
        }

        // eslint-disable-next-line
    }, [currentNode]);


    function renderWarningPopup() {
        let warningPopup = null;

        if (currentNode && currentNode.WarningPopupIdentifiers && currentNode.WarningPopupIdentifiers.length) {
            for (const nodeId of currentNode.WarningPopupIdentifiers) {
                const popupNode = procedureStore.getNodeById(nodeId, isPreview);
                if (!popupNode) {
                    continue;
                }
                if (!popupNode.approved) {
                    warningPopup = popupNode;
                    break;
                }
            }
        }

        if (!warningPopup) {
            return null;
        }


        function onPopupApproved() {
            warningPopup.approved = true;
            if (isPreview) {
                procedureStore.currentPreviewedNode = currentNode;
            } else {
                procedureStore.addWarningAcknowledgementToSession(getProcedureText(warningPopup, "StepHeader", currentProcedure));
                procedureStore.currentNode = currentNode;
            }
            procedureStore.shutUp();
            procedureStore.escapeAndSpeakDescription(isPreview);
        }

        function closeFullscreenPreviewedFile() {
            procedureStore.warningPopupPreviewedFile = null;
        }

        const hasFiles = getNodeMedia(warningPopup, "NodeMediaFilesRefs", procedureStore.currentProcedure).FileRefs.length;
        const {warningPopupPreviewedFile} = procedureStore;

        return (
            <ConditionalComponent if={!!warningPopup}>
                <div className="warning-popup-background"/>

                <ConditionalComponent if={generalStore.isInFullscreen && !!warningPopupPreviewedFile}>
                    <div
                        className="popup-media-preview homemade"
                        onClick={closeFullscreenPreviewedFile}
                    >
                        <div className="homemade-popup-content">
                            {Utility.renderFilePreview(warningPopupPreviewedFile, generalStore.translate)}
                        </div>
                    </div>
                </ConditionalComponent>
                <div className='warning-popup' style={{borderColor: warningPopup.Color}}>
                    <div
                        className='warning-popup-header'
                        dangerouslySetInnerHTML={{__html: getProcedureText(warningPopup, "StepHeader", currentProcedure)}}
                        style={{backgroundColor: warningPopup.Color}}
                    />
                    <div className='warning-body-media-container'>
                        <div
                            className='warning-popup-body'
                            dangerouslySetInnerHTML={{__html: getProcedureText(warningPopup, "StepText", currentProcedure)}}
                        />
                        <ConditionalComponent if={!procedureStore.sessionViewerOpen}>
                            <TtsMuteButton isPreview={isPreview}/>
                        </ConditionalComponent>
                        <ConditionalComponent if={!!hasFiles}>
                            <PopupMediaSelect currentNode={warningPopup} currentProcedure={currentProcedure}/>
                        </ConditionalComponent>
                    </div>
                    <div className='warning-popup-button-container'>
                        <Button
                            variant='contained'
                            onClick={onPopupApproved}
                            style={{
                                borderColor: warningPopup.Color,
                                color: warningPopup.Color
                            }}
                        >
                            <span
                                dangerouslySetInnerHTML={{__html: getProcedureText(warningPopup, "ConformationText", currentProcedure)}}
                            />
                        </Button>
                    </div>
                </div>
            </ConditionalComponent>
        )
    }

    function renderRegularNode() {
        if (!currentNode) {
            return null;
        }

        const mobileNotTablet = Utility.isMobile() && !isTablet;
        const mediaFiles = getNodeMedia(currentNode, "NodeMediaFilesRefs", currentProcedure).FileRefs;
        const file = mediaFiles.length && mediaFiles[procedureStore.currentMedia];
        const display = Utility.renderFilePreview(file, generalStore.translate);
        const isDescriptionRight = !mobileNotTablet && !!procedureStore.showSupportingText && !!getProcedureText(currentNode, "StepText", currentProcedure) && procedureNodeStyleEnum.RIGHT === currentNode?.Style?.Description;
        const isDescriptionLeft = !mobileNotTablet && !!procedureStore.showSupportingText && !!getProcedureText(currentNode, "StepText", currentProcedure) && procedureNodeStyleEnum.LEFT === currentNode?.Style?.Description;
        return (
            <div
                className={"procedure-media-container" + (isDescriptionRight ? ' description-right' : '') + (isDescriptionLeft ? ' description-left' : '')}>
                <ConditionalComponent if={procedureStore.currentMedia >= 0}>
                    <IconButton
                        className='media-select-action start'
                        onClick={() => !procedureStore.sessionViewerOpen && procedureStore.currentMedia--}
                        disabled={!procedureStore.currentMedia}
                    >
                        <ArrowForwardIosIcon/>
                    </IconButton>
                    <div className="procedure-media">
                        <div className="main-media-container">
                            {display}
                        </div>
                    </div>
                    <IconButton
                        className='media-select-action end'
                        onClick={() => !procedureStore.sessionViewerOpen && procedureStore.currentMedia++}
                        disabled={mediaFiles.length - 1 === procedureStore.currentMedia}
                    >
                        <ArrowForwardIosIcon/>
                    </IconButton>
                </ConditionalComponent>

            </div>
        );
    }

    function renderSummary(showIntro) {
        return (
            <ConditionalComponent if={!showIntro}>
                <ProcedureSummary isPreview={isPreview} position={procedureNodeStyleEnum.TOP}/>
                <ProcedureSummary isPreview={isPreview} position={procedureNodeStyleEnum.FLOAT} fromEditor={fromEditor}/>
                <ProcedureSummary isPreview={isPreview} position={procedureNodeStyleEnum.BOTTOM}/>
                <ProcedureSummary isPreview={isPreview} position={procedureNodeStyleEnum.RIGHT}/>
                <ProcedureSummary isPreview={isPreview} position={procedureNodeStyleEnum.LEFT}/>
            </ConditionalComponent>
        );
    }

    function renderIntroNode(showIntro) {
        return (
            <ConditionalComponent if={showIntro}>
                <ProcedureIntro isPreview={isPreview}/>
            </ConditionalComponent>
        );
    }

    return (
        <div className="procedure-main-container">
            <ConditionalComponent if={!!currentNode && !!currentNode.notAllowed}>
                <div className='not-allowed'>
                    <div>
                        {generalStore.translate('noPermissionViewNodesSentence')}<br/>
                        {!isEndNode
                            ? <>{generalStore.translate('pressToSkipSentence1_beforeIcon')} <i
                                className="fas fa-arrow-right"/> {generalStore.translate('pressToSkipSentence2_afterIcon')}</>
                            : <>{generalStore.translate('pressToEndProcedureSentence1_beforeIcon')}
                                <CancelPresentationIcon
                                    fontWeight={600}/> {generalStore.translate('pressToEndProcedureSentence2_afterIcon')}</>
                        }
                    </div>
                </div>
            </ConditionalComponent>
            <ConditionalComponent if={!isEndNode}>
                {renderSummary(showIntro)}
                {renderWarningPopup()}
                {renderRegularNode()}
                {renderIntroNode(showIntro)}
            </ConditionalComponent>
            <ConditionalComponent if={isEndNode}>
                <EndNode isPreview={isPreview}/>
            </ConditionalComponent>
        </div>
    )
});

ProcedureMain.propTypes = {
    isPreview: PropTypes.bool.isRequired,
}

export default ProcedureMain
